/**
 * W1-11a (app-registry-keymap): default UserSettings, first-run profiles and the settings schema
 * (DESIGN.md §2.4, §4.2, §4.20, §5.15).
 *
 * - `createDefaultSettings()`: product defaults; the locale comes from navigator.language (zh* -> 'zh-CN', else
 *   'en'). Newcomer-safe: Guided level, sentence hints, first-use tips and the tour on, Default keymap.
 * - `firstRunSettings(base, answer)`: what the first-run question sets (language, level, keymap preset, hint-bar
 *   style, tour, and the RPE-preset editing conventions for RPE users). Nothing defaults to Full for newcomers.
 * - `SETTINGS_SCHEMA` + `sanitizeSettings`: every stored value is validated against its type, allowed values and
 *   range before use (corrupt or future values never reach the app; numbers out of range are clamped, unknown keys
 *   dropped, missing fields of nested objects filled from the defaults).
 */

import type { Json } from '../../core/util/types';
import type { PhiraPlayerSettings } from '../../core/engine/types';
import type { CheckThresholds } from '../../core/check/types';
import type { NoteTemplate } from '../../core/model/types';
import type { Locale } from '../../i18n/types';
import type { KeyBinding, KeymapPresetId } from '../registry/types';
import type { FirstRunAnswer, SettingsSection, UserSettings } from './types';

// ---------------------------------------------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------------------------------------------

/** Phira's own player defaults (preview-only, never written to a chart). */
export const DEFAULT_PLAYER_SETTINGS: PhiraPlayerSettings = {
  aspectOverride: null,
  noteScale: 1,
  doubleHint: true,
  particles: true,
  aggressiveCull: true,
  strictAutoplayHitColor: true,
  msaa: false,
  apFcIndicator: true,
};

/** RPE auto-correct parameters (DESIGN.md §4.20: X 700, collision 200 px, read 0.3 s, drag 0.04 s, combo 0.16 s). */
export const DEFAULT_CHECK_THRESHOLDS: CheckThresholds = {
  xMax: 700,
  collisionPx: 200,
  readTimeSec: 0.3,
  dragWindowSec: 0.04,
  comboWindowSec: 0.16,
  nearSimultaneousSec: 0.002,
};

/**
 * Checker rules that start switched off: the ids of the rules with `CheckRule.defaultEnabled: false`
 * (settingsService.test.ts keeps the two in sync). The settings store only DISABLED rules, so a rule starts off by
 * being in this default; a user who turns it on keeps it on.
 */
export const DEFAULT_DISABLED_RULES: readonly string[] = ['ST-06'];

/** New-note defaults (RPE's: above, real, speed 1, no offset, size 1, opaque, no visible-time limit). */
export const DEFAULT_NOTE_TEMPLATE: NoteTemplate = {
  above: true,
  fake: false,
  speed: 1,
  yOffset: 0,
  size: 1,
  alpha: 255,
  visibleTime: null,
};

/** UserSettings.general.locale rule: a `zh*` language -> 'zh-CN', anything else (or none) -> 'en'. */
export function localeFromLanguage(language: string | undefined | null): Locale {
  return (language ?? '').trim().toLowerCase().startsWith('zh') ? 'zh-CN' : 'en';
}

/** The default UI locale from navigator.language (the browser's UI language), per the contracted rule. */
export function browserLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en';
  return localeFromLanguage(navigator.language);
}

export interface DefaultSettingsOptions {
  /** Defaults to the browser locale. */
  readonly locale?: Locale;
}

export function createDefaultSettings(opts: DefaultSettingsOptions = {}): UserSettings {
  return {
    general: {
      level: 'guided',
      locale: opts.locale ?? browserLocale(),
      theme: 'dark',
      reducedMotion: 'system',
      uiScale: 1,
      firstRunDone: false,
      hintBar: 'sentences',
      firstUseTips: true,
      tourDone: false,
      latencyHintShown: false,
    },
    editing: {
      defaultSnapDiv: 4,
      defaultXGrid: 11,
      scrollSensitivity: 1,
      snapPlayheadOnPause: false,
      playheadPosition: 0.25,
      jumpToChangeOnUndo: true,
      noteDefaults: DEFAULT_NOTE_TEMPLATE,
      pasteMode: 'ghost',
      pasteAnchor: 'mouse',
      beatDisplay: 'colon',
      beatSyntax: 'default',
      units: 'friendly',
      autoKeyDefault: false,
      layout: 'compose',
    },
    audio: {
      volumes: { master: 1, music: 1, hitsounds: 0.8, metronome: 0.8 },
      latency: { calibrationMs: 0, hitsoundDelayMs: { tapFlick: 0, drag: 0 } },
      spaceStops: 'stay',
      preservePitch: true,
    },
    preview: {
      player: DEFAULT_PLAYER_SETTINGS,
      defaultMode: 'edit',
      defaultSkin: null,
      renderScale: 'auto',
      fpsCap: 0,
      userFonts: { ui: null, pgr: null },
    },
    keyboard: { preset: 'default', altFree: false, overrides: [] },
    checker: { thresholds: DEFAULT_CHECK_THRESHOLDS, disabledRules: [...DEFAULT_DISABLED_RULES] },
    ext: {},
  };
}

// ---------------------------------------------------------------------------------------------------------------
// First run (DESIGN.md §4.2)
// ---------------------------------------------------------------------------------------------------------------

/** The first-run question's answer (DialogMap['firstRun'] result). */
export type { FirstRunAnswer } from './types';

/** Editing conventions that come with a keymap preset (RPE: immediate paste, `12.1` = 12 + 1/snap, raw units). */
export function presetConventions(
  preset: KeymapPresetId,
): Pick<UserSettings['editing'], 'pasteMode' | 'beatSyntax' | 'units'> {
  return preset === 'rpe'
    ? { pasteMode: 'immediate', beatSyntax: 'rpe', units: 'rpe' }
    : { pasteMode: 'ghost', beatSyntax: 'default', units: 'friendly' };
}

/**
 * Settings after the first-run question:
 *  - "I'm new to charting": Guided, Default keys, sentence hints, tour;
 *  - "I've used RPE": Full, RPE keys and conventions, compact key hints, no beginner tour ("For RPE users" guide);
 *  - "I've used other editors": Full, Default keys, compact key hints, no beginner tour.
 */
export function firstRunSettings(base: UserSettings, answer: FirstRunAnswer): UserSettings {
  const guided = answer.level === 'guided';
  return {
    ...base,
    general: {
      ...base.general,
      level: answer.level,
      locale: answer.locale,
      firstRunDone: true,
      hintBar: guided ? 'sentences' : 'keys',
      firstUseTips: true,
      tourDone: !guided,
    },
    editing: { ...base.editing, ...presetConventions(answer.preset) },
    keyboard: { ...base.keyboard, preset: answer.preset },
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Schema and sanitising
// ---------------------------------------------------------------------------------------------------------------

export type SettingSpec =
  | { readonly k: 'bool' }
  | { readonly k: 'num'; readonly min: number; readonly max: number; readonly int?: boolean }
  | { readonly k: 'enum'; readonly values: readonly Json[] }
  | { readonly k: 'str'; readonly max?: number }
  | { readonly k: 'nullable'; readonly spec: SettingSpec }
  | { readonly k: 'obj'; readonly fields: Readonly<Record<string, SettingSpec>> }
  | { readonly k: 'strings' }
  | { readonly k: 'bindings' }
  | { readonly k: 'json' };

const bool: SettingSpec = { k: 'bool' };
const num = (min: number, max: number, int = false): SettingSpec => ({ k: 'num', min, max, int });
const oneOf = (...values: readonly Json[]): SettingSpec => ({ k: 'enum', values });
const nullable = (spec: SettingSpec): SettingSpec => ({ k: 'nullable', spec });
const obj = (fields: Readonly<Record<string, SettingSpec>>): SettingSpec => ({ k: 'obj', fields });
const ms: SettingSpec = num(-1000, 1000);
const volume: SettingSpec = num(0, 1);

/** Type, allowed values and range of every setting (`ext` values are free JSON). */
export const SETTINGS_SCHEMA: {
  readonly [S in Exclude<SettingsSection, 'ext'>]: Readonly<Record<keyof UserSettings[S], SettingSpec>>;
} = {
  general: {
    level: oneOf('guided', 'full'),
    locale: oneOf('en', 'zh-CN'),
    theme: oneOf('dark', 'light', 'highContrast'),
    reducedMotion: oneOf('system', 'on', 'off'),
    uiScale: num(0.75, 2),
    firstRunDone: bool,
    hintBar: oneOf('sentences', 'keys', 'off'),
    firstUseTips: bool,
    tourDone: bool,
    latencyHintShown: bool,
  },
  editing: {
    defaultSnapDiv: num(1, 1920, true),
    defaultXGrid: num(1, 200, true),
    scrollSensitivity: num(0.05, 20),
    snapPlayheadOnPause: bool,
    playheadPosition: num(0, 0.9),
    jumpToChangeOnUndo: bool,
    noteDefaults: obj({
      above: bool,
      fake: bool,
      speed: num(-1e6, 1e6),
      yOffset: num(-1e6, 1e6),
      size: num(0, 1e3),
      alpha: num(0, 255, true),
      visibleTime: nullable(num(0, 1e6)),
    }),
    pasteMode: oneOf('ghost', 'immediate'),
    pasteAnchor: oneOf('mouse', 'playhead'),
    beatDisplay: oneOf('colon', 'plus', 'bar'),
    beatSyntax: oneOf('default', 'rpe'),
    units: oneOf('friendly', 'rpe'),
    autoKeyDefault: bool,
    layout: oneOf('compose', 'animate', 'review', 'laptop'),
  },
  audio: {
    volumes: obj({ master: volume, music: volume, hitsounds: volume, metronome: volume }),
    latency: obj({ calibrationMs: ms, hitsoundDelayMs: obj({ tapFlick: ms, drag: ms }) }),
    spaceStops: oneOf('stay', 'return'),
    preservePitch: bool,
  },
  preview: {
    player: obj({
      aspectOverride: nullable(num(0.1, 10)),
      noteScale: num(0.1, 10),
      doubleHint: bool,
      particles: bool,
      aggressiveCull: bool,
      strictAutoplayHitColor: bool,
      msaa: bool,
      apFcIndicator: bool,
    }),
    defaultMode: oneOf('edit', 'clean'),
    defaultSkin: nullable({ k: 'str', max: 512 }),
    renderScale: oneOf('auto', 1, 1.5, 2),
    fpsCap: oneOf(0, 30, 60, 120),
    userFonts: obj({ ui: nullable({ k: 'str', max: 512 }), pgr: nullable({ k: 'str', max: 512 }) }),
  },
  keyboard: {
    preset: oneOf('default', 'rpe'),
    altFree: bool,
    overrides: { k: 'bindings' },
  },
  checker: {
    thresholds: obj({
      xMax: num(0, 1e5),
      collisionPx: num(0, 1e5),
      readTimeSec: num(0, 60),
      dragWindowSec: num(0, 10),
      comboWindowSec: num(0, 10),
      nearSimultaneousSec: num(0, 1),
    }),
    disabledRules: { k: 'strings' },
  },
};

function isRecord(v: unknown): v is Readonly<Record<string, unknown>> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function isJson(v: unknown, depth = 0): v is Json {
  if (depth > 64) return false;
  if (v === null || typeof v === 'boolean' || typeof v === 'string') return true;
  if (typeof v === 'number') return Number.isFinite(v);
  if (Array.isArray(v)) return v.every((x) => isJson(x, depth + 1));
  if (isRecord(v)) return Object.values(v).every((x) => isJson(x, depth + 1));
  return false;
}

/** A stored key binding, or null when malformed (shape only; chords and whens are validated by the keymap). */
export function sanitizeBinding(v: unknown): KeyBinding | null {
  if (!isRecord(v)) return null;
  const { chord, command, args, when, altFree, mac, remove } = v;
  if (typeof chord !== 'string' || chord === '' || typeof command !== 'string' || command === '') return null;
  if (args !== undefined && !isJson(args)) return null;
  if (when !== undefined && typeof when !== 'string') return null;
  if (altFree !== undefined && altFree !== null && typeof altFree !== 'string') return null;
  if (mac !== undefined && mac !== null && typeof mac !== 'string') return null;
  if (remove !== undefined && remove !== true) return null;
  return {
    chord,
    command,
    ...(args === undefined ? {} : { args }),
    ...(when === undefined ? {} : { when }),
    ...(altFree === undefined ? {} : { altFree }),
    ...(mac === undefined ? {} : { mac }),
    ...(remove === true ? { remove: true as const } : {}),
  };
}

/** Value conforming to `spec`: `raw` when valid (numbers clamped, objects merged field by field), else `fallback`. */
export function sanitizeValue(spec: SettingSpec, raw: unknown, fallback: unknown): unknown {
  switch (spec.k) {
    case 'bool':
      return typeof raw === 'boolean' ? raw : fallback;
    case 'num': {
      if (typeof raw !== 'number' || !Number.isFinite(raw)) return fallback;
      const v = Math.min(spec.max, Math.max(spec.min, raw));
      return spec.int === true ? Math.round(v) : v;
    }
    case 'enum':
      return spec.values.includes(raw as Json) ? raw : fallback;
    case 'str':
      return typeof raw === 'string' && raw.length <= (spec.max ?? Infinity) ? raw : fallback;
    case 'nullable':
      return raw === null ? null : sanitizeValue(spec.spec, raw, fallback);
    case 'obj': {
      if (!isRecord(raw)) return fallback;
      const base = isRecord(fallback) ? fallback : {};
      const out: Record<string, unknown> = {};
      for (const [field, fieldSpec] of Object.entries(spec.fields)) {
        out[field] = sanitizeValue(fieldSpec, raw[field], base[field]);
      }
      return out;
    }
    case 'strings':
      return Array.isArray(raw) ? [...new Set(raw.filter((x): x is string => typeof x === 'string'))] : fallback;
    case 'bindings':
      return Array.isArray(raw) ? raw.map(sanitizeBinding).filter((b): b is KeyBinding => b !== null) : fallback;
    case 'json':
      return isJson(raw) ? raw : fallback;
  }
}

/** The spec of a `section.key` path (undefined for unknown paths and for `ext.*`). */
export function specOf(section: string, key: string): SettingSpec | undefined {
  if (!Object.hasOwn(SETTINGS_SCHEMA, section)) return undefined;
  const fields = SETTINGS_SCHEMA[section as Exclude<SettingsSection, 'ext'>] as Readonly<Record<string, SettingSpec>>;
  return Object.hasOwn(fields, key) ? fields[key] : undefined;
}

/**
 * Settings built from stored (possibly partial, old or corrupt) data on top of `defaults`. Only known keys survive;
 * `ext` keeps every JSON value.
 */
export function sanitizeSettings(raw: unknown, defaults: UserSettings): UserSettings {
  const stored = isRecord(raw) ? raw : {};
  const out: Record<string, unknown> = {};
  for (const section of Object.keys(SETTINGS_SCHEMA) as Array<Exclude<SettingsSection, 'ext'>>) {
    const fields = SETTINGS_SCHEMA[section] as Readonly<Record<string, SettingSpec>>;
    const def = defaults[section] as Readonly<Record<string, unknown>>;
    const src = isRecord(stored[section]) ? stored[section] : {};
    const sec: Record<string, unknown> = {};
    for (const [key, spec] of Object.entries(fields)) {
      sec[key] = Object.hasOwn(src, key) ? sanitizeValue(spec, src[key], def[key]) : def[key];
    }
    out[section] = sec;
  }
  const ext: Record<string, Json> = { ...defaults.ext };
  if (isRecord(stored.ext)) {
    for (const [key, value] of Object.entries(stored.ext)) if (isJson(value)) ext[key] = value;
  }
  out.ext = ext;
  return out as unknown as UserSettings;
}
